<template>
    <BasePopover
        as-child
        side="bottom"
        align="end"
        :side-offset="16"
        :collision-padding="16"
        :content-class="`${$style.surface} w-96 max-w-[calc(100vw-2rem)] p-5`"
        :open="open"
        @update:open="updateOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>
        <template #content>
            <form data-testid="development-journeys-feedback-dialog" class="flex flex-col gap-6" @submit.prevent="submit">
                <h2 class="title-body text-ink">{{ t("developmentJourneys.feedbackDialog.heading") }}</h2>
                <BaseTextarea
                    id="development-journeys-feedback-comment"
                    v-model:input="comment"
                    data-testid="development-journeys-feedback-comment"
                    :aria-label="t('developmentJourneys.feedbackDialog.feedbackLabel')"
                    :placeholder="t('developmentJourneys.feedbackDialog.feedbackLabel')"
                    :maxlength="FEEDBACK_TEXT_MAX_LENGTH"
                    required
                    :disabled="isSubmitting"
                    disable-autosize
                    show-resize-handle
                    class="min-h-32 resize-y rounded-lg border border-control-border bg-control-bg p-3 text-control-text placeholder:text-control-placeholder transition-colors"
                    :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'" />

                <div role="alert" aria-atomic="true">
                    <BaseNotification v-if="errorMessage" role="none" theme="error" :text="errorMessage" hide-dismiss />
                </div>

                <div class="flex justify-end gap-3">
                    <JourneyButton type="button" theme="ghost" size="md" :disabled="isSubmitting" @click="close"> {{ t("common.cancel") }} </JourneyButton>
                    <JourneyButton type="submit" theme="primary" size="md" :disabled="!canSubmit" :aria-busy="isSubmitting">
                        {{ isSubmitting ? t("developmentJourneys.feedbackDialog.sending") : t("developmentJourneys.feedbackDialog.send") }}
                    </JourneyButton>
                </div>
            </form>
        </template>
    </BasePopover>
</template>

<script setup lang="ts">
import type { DevelopmentJourneysFeedbackPayload } from "~vue/api/generated/feedback";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import JourneyButton from "~vue/pages/developmentJourneys/DevelopmentJourneyButton.vue";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const FEEDBACK_TEXT_MAX_LENGTH = 2_000;

const props = defineProps<{
    isSubmitting: boolean;
    errorMessage: string | null;
}>();
const emit = defineEmits<{
    submit: [payload: DevelopmentJourneysFeedbackPayload];
}>();
const open = defineModel<boolean>("open", { default: false });
const comment = ref("");
const canSubmit = computed(() => !props.isSubmitting && comment.value.trim().length > 0);

const close = () => {
    if (!props.isSubmitting) {
        open.value = false;
    }
};

const updateOpen = (isOpen: boolean) => {
    if (props.isSubmitting && !isOpen) {
        return;
    }
    open.value = isOpen;
};

const submit = () => {
    if (!canSubmit.value) {
        return;
    }
    emit("submit", { comment: comment.value.trim() });
};

watch(open, (isOpen) => {
    if (!isOpen) {
        comment.value = "";
    }
});
</script>

<style module>
@reference "../../../../css/tailwind.css";

@layer components {
    .surface {
        --color-popover-bg: var(--tonal-neutral-25);
    }
}
</style>
